/**
 * ErrorBoundaryWrapper is a reusable component that catches JavaScript errors in its child component tree.
 *
 * - Reports caught errors to NewRelic, including an optional errorSource for context.
 * - Renders an empty fallback UI when an error occurs or while loading suspenseful content.
 * - Use errorSource to specify where the error originated for better NewRelic tracking.
 *
 * @param errorSource Optional string to identify the error's source file or component.
 * @param children React nodes to render within the error boundary.
 */
import React, { ReactElement, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

export const onError = (error: Error, errorSource?: string): void => {
  globalThis?.window?.newrelic?.noticeError?.(
    error,
    errorSource ? { source: errorSource } : {},
  )
}

export const ErrorBoundaryWrapper = ({
  errorSource,
  children,
}: {
  errorSource?: string
  children: React.ReactNode
}): ReactElement => {
  return (
    <ErrorBoundary
      fallback={<></>}
      onError={(error: Error) => onError(error, errorSource)}
    >
      <Suspense fallback={<></>}>{children}</Suspense>
    </ErrorBoundary>
  )
}
